import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner, Form } from "react-bootstrap";

const PagoConfirmModal = ({
  show,
  onHide,
  selectedRows,
  totalPagar,
  isProcessing,
  onConfirm,
}) => {
  const [editableAmounts, setEditableAmounts] = useState({});

  // Initialize/Update editable amounts when show or selectedRows changes
  useEffect(() => {
    if (show) {
      const initial = {};
      Object.values(selectedRows).forEach((p) => {
        // Monto del consumo base (si aplica)
        if (p.tipo === "CONSUMO") {
          const subtotalConsumo =
            (p.valores?.total || 0) - (p.valores?.deuda_extra || 0);
          initial[`c_${p.id_consumo}`] = subtotalConsumo;
        } else if (p.tipo === "EXTRA") {
          const val = parseFloat(p.saldo_pendiente || p.valores?.total || 0);
          initial[`e_${p.id_deuda_detalle}`] = val > 0 ? val.toFixed(2) : "0.00";
        }

        // Montos de deudas extras asociadas
        if (p.lista_deudas_extras) {
          p.lista_deudas_extras.forEach((extra) => {
            const val = parseFloat(extra.valor_pendiente || 0);
            initial[`e_${extra.id_deuda_detalle}`] = val > 0 ? val.toFixed(2) : "0.00";
          });
        }
      });
      setEditableAmounts(initial);
    }
  }, [show, selectedRows]);

  const handleAmountChange = (id, rawValue) => {
    // Permite escribir cualquier texto con total fluidez (ej: "5", "5.", "5.50")
    setEditableAmounts((prev) => ({
      ...prev,
      [id]: rawValue,
    }));
  };

  const handleAmountBlur = (id, maxVal) => {
    setEditableAmounts((prev) => {
      const raw = prev[id];
      let val = parseFloat(raw);
      if (raw === "" || isNaN(val) || val <= 0) {
        return { ...prev, [id]: maxVal.toFixed(2) };
      }
      if (Math.round(val * 100) > Math.round(maxVal * 100)) {
        return { ...prev, [id]: maxVal.toFixed(2) };
      }
      return { ...prev, [id]: val.toFixed(2) };
    });
  };

  // Validación y cálculo de errores en tiempo real
  const amountErrors = {};
  Object.values(selectedRows).forEach((p) => {
    if (p.tipo === "EXTRA") {
      const key = `e_${p.id_deuda_detalle}`;
      const raw = editableAmounts[key];
      const val = parseFloat(raw);
      const maxVal = parseFloat(p.saldo_pendiente || p.valores?.total || 0);
      if (raw === "" || isNaN(val) || val <= 0) {
        amountErrors[key] = "Ingrese un monto mayor a $0.00";
      } else if (Math.round(val * 100) > Math.round(maxVal * 100)) {
        amountErrors[key] = `Máximo permitido: $${maxVal.toFixed(2)}`;
      }
    }

    if (p.lista_deudas_extras) {
      p.lista_deudas_extras.forEach((extra) => {
        const key = `e_${extra.id_deuda_detalle}`;
        const raw = editableAmounts[key];
        const val = parseFloat(raw);
        const maxVal = parseFloat(extra.valor_pendiente || 0);
        if (raw === "" || isNaN(val) || val <= 0) {
          amountErrors[key] = "Ingrese un monto mayor a $0.00";
        } else if (Math.round(val * 100) > Math.round(maxVal * 100)) {
          amountErrors[key] = `Máximo permitido: $${maxVal.toFixed(2)}`;
        }
      });
    }
  });

  const hasErrors = Object.keys(amountErrors).length > 0;

  const currentTotal = Object.values(editableAmounts)
    .reduce((acc, val) => acc + (parseFloat(val) || 0), 0)
    .toFixed(2);

  const handleLocalConfirm = () => {
    if (isProcessing) return;

    // Validación estricta antes de enviar el pago
    for (const p of Object.values(selectedRows)) {
      if (p.tipo === "EXTRA") {
        const monto = parseFloat(editableAmounts[`e_${p.id_deuda_detalle}`] || 0);
        const maxVal = parseFloat(p.saldo_pendiente || p.valores?.total || 0);
        if (Math.round(monto * 100) > Math.round(maxVal * 100)) {
          alert(`El abono para "${p.descripcion}" ($${monto.toFixed(2)}) no puede ser mayor al saldo pendiente ($${maxVal.toFixed(2)}).`);
          return;
        }
        if (monto <= 0 || isNaN(monto)) {
          alert(`El abono para "${p.descripcion}" debe ser mayor a $0.00.`);
          return;
        }
      }

      if (p.lista_deudas_extras && p.lista_deudas_extras.length > 0) {
        for (const extra of p.lista_deudas_extras) {
          const monto = parseFloat(editableAmounts[`e_${extra.id_deuda_detalle}`] || 0);
          const maxVal = parseFloat(extra.valor_pendiente || 0);
          if (Math.round(monto * 100) > Math.round(maxVal * 100)) {
            alert(`El abono para "${extra.titulo}" ($${monto.toFixed(2)}) no puede ser mayor al saldo pendiente ($${maxVal.toFixed(2)}).`);
            return;
          }
          if (monto <= 0 || isNaN(monto)) {
            alert(`El abono para "${extra.titulo}" debe ser mayor a $0.00.`);
            return;
          }
        }
      }
    }

    const flattenedRows = [];
    const processedExtraIds = new Set();

    Object.values(selectedRows).forEach((p) => {
      if (p.tipo === "CONSUMO") {
        flattenedRows.push({
          ...p,
          monto_pago: editableAmounts[`c_${p.id_consumo}`],
          tipo: "CONSUMO",
        });
      } else if (p.tipo === "EXTRA") {
        const monto = parseFloat(editableAmounts[`e_${p.id_deuda_detalle}`] || 0);
        if (monto > 0 && !processedExtraIds.has(p.id_deuda_detalle)) {
          processedExtraIds.add(p.id_deuda_detalle);
          flattenedRows.push({
            ...p,
            monto_pago: monto,
            tipo: "EXTRA",
          });
        }
      }

      if (p.lista_deudas_extras && p.lista_deudas_extras.length > 0) {
        p.lista_deudas_extras.forEach((extra) => {
          const monto = parseFloat(editableAmounts[`e_${extra.id_deuda_detalle}`] || 0);
          if (monto > 0 && !processedExtraIds.has(extra.id_deuda_detalle)) {
            processedExtraIds.add(extra.id_deuda_detalle);
            flattenedRows.push({
              ...p,
              id_deuda_detalle: extra.id_deuda_detalle,
              descripcion: extra.titulo,
              monto_pago: monto,
              tipo: "EXTRA",
              lecturas: { anterior: 0, actual: 0 },
              consumos: { m3: 0, excesoM3: 0 },
              valores: { total: extra.valor_pendiente, mora: 0, reconexion: 0 },
            });
          }
        });
      }
    });
    onConfirm(flattenedRows);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="!max-w-[95vw] !w-[95vw]"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="text-xl font-bold">
          Registros Seleccionados para Pagar
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="table-responsive border rounded-lg overflow-x-auto my-4">
          <table className="table mb-0 align-middle w-full min-w-[1000px]">
            <thead className="bg-gray-50 border-b">
              <tr className="text-sm font-bold text-gray-900">
                <th className="py-3 px-4">Código / Tipo</th>
                <th className="py-3 px-4">Descripción</th>
                <th className="py-3 px-4">Información Lectura</th>
                <th className="py-3 px-4">Mes</th>
                <th className="py-3 px-4">Exceso</th>
                <th className="py-3 px-4">Mora</th>
                <th className="py-3 px-4">Reconexión</th>
                <th className="py-3 px-4 text-right">Valor Item</th>
                <th className="py-3 px-4 text-right" style={{ width: "150px" }}>
                  Monto a Pagar
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Object.values(selectedRows).map((p, i) => {
                const subtotalConsumo =
                  p.tipo === "CONSUMO"
                    ? (p.valores?.total || 0) - (p.valores?.deuda_extra || 0)
                    : 0;

                return (
                  <React.Fragment key={i}>
                    {/* Fila Principal de Consumo */}
                    {p.tipo === "CONSUMO" && (
                      <tr className="text-sm bg-white">
                        <td className="py-3 px-4 font-bold text-blue-600">
                          {p.codigo_medidor || p.medidor?.codigo_medidor}
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-bold text-gray-900">
                            {p.nombre_cliente ||
                              (p.medidor?.cliente
                                ? `${p.medidor.cliente.nombres} ${p.medidor.cliente.apellidos}`
                                : "")}
                          </div>
                          <div className="text-xs text-gray-500">
                            CONSUMO DE AGUA
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-xs text-gray-500">
                            ANT: {p.lecturas?.anterior} / ACT:{" "}
                            {p.lecturas?.actual}
                          </div>
                          <div className="font-bold text-blue-600">
                            {p.consumos?.m3} m³
                          </div>
                        </td>
                        <td className="py-3 px-4 uppercase font-medium">
                          {p.mes}
                        </td>
                        <td className="py-3 px-4 text-orange-600 font-medium">
                          ${p.valores?.valorExceso || "0.00"}
                        </td>
                        <td className="py-3 px-4 text-red-600">
                          ${p.valores?.mora || "0.00"}
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          ${p.valores?.reconexion || "0.00"}
                        </td>
                        <td className="py-3 px-4 text-right font-bold">
                          ${subtotalConsumo.toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-bold text-blue-700">
                            ${subtotalConsumo.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    )}

                    {/* Filas de Deudas Extras */}
                    {p.lista_deudas_extras?.map((extra) => (
                      <tr
                        key={`extra_${extra.id_deuda_detalle}`}
                        className="text-sm bg-amber-50/30"
                      >
                        <td className="py-2 px-4 text-center">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 uppercase">
                            DEUDA
                          </span>
                        </td>
                        <td className="py-2 px-4 pl-8 border-l-4 border-amber-400">
                          <div className="font-medium text-amber-900">
                            {extra.titulo}
                          </div>
                        </td>
                        <td className="py-2 px-4 text-gray-400">---</td>
                        <td className="py-2 px-4 text-gray-400">---</td>
                        <td className="py-2 px-4 text-gray-400">---</td>
                        <td className="py-2 px-4 text-gray-400">---</td>
                        <td className="py-2 px-4 text-right text-gray-500">
                          ${extra.valor_pendiente.toFixed(2)}
                        </td>
                        <td className="py-2 px-4 text-right">
                          <Form.Control
                            type="number"
                            size="sm"
                            className={`w-24 text-right font-bold ml-auto ${
                              amountErrors[`e_${extra.id_deuda_detalle}`]
                                ? "border-red-500 bg-red-50 text-red-700 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                                : "border-amber-200"
                            }`}
                            value={
                              editableAmounts[`e_${extra.id_deuda_detalle}`] ?? ""
                            }
                            onChange={(e) =>
                              handleAmountChange(
                                `e_${extra.id_deuda_detalle}`,
                                e.target.value
                              )
                            }
                            onBlur={() =>
                              handleAmountBlur(
                                `e_${extra.id_deuda_detalle}`,
                                parseFloat(extra.valor_pendiente || 0)
                              )
                            }
                            step="0.01"
                            min="0.01"
                            max={parseFloat(extra.valor_pendiente || 0)}
                          />
                          {amountErrors[`e_${extra.id_deuda_detalle}`] ? (
                            <span className="text-[10px] text-red-600 font-bold block mt-0.5">
                              {amountErrors[`e_${extra.id_deuda_detalle}`]}
                            </span>
                          ) : (
                            <span className="text-[10px] text-amber-800 font-medium block mt-0.5">
                              Máx: ${parseFloat(extra.valor_pendiente || 0).toFixed(2)}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}

                    {/* Fila Independiente de Deuda Extra */}
                    {p.tipo === "EXTRA" && (
                      <tr className="text-sm bg-indigo-50/20">
                        <td className="py-3 px-4 text-center">
                          {p.codigo_medidor && p.codigo_medidor !== '---' && (
                            <span className="font-bold text-gray-700 block text-xs">{p.codigo_medidor}</span>
                          )}
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700 uppercase">
                            EXTRA
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-bold text-gray-900">
                            {p.nombre_cliente ||
                              (p.medidor?.cliente
                                ? `${p.medidor.cliente.nombres} ${p.medidor.cliente.apellidos}`
                                : "")}
                          </div>
                          <div className="font-medium text-indigo-800">
                            {p.descripcion}
                          </div>
                          {p.valor_total_original ? (
                            <div className="text-[11px] text-gray-500">
                              Total Deuda: ${p.valor_total_original.toFixed(2)} | Ya Abonado: ${(p.valor_ya_pagado || 0).toFixed(2)}
                            </div>
                          ) : null}
                        </td>
                        <td className="py-3 px-4 text-gray-400">---</td>
                        <td className="py-3 px-4 uppercase text-gray-400">
                          {p.mes}
                        </td>
                        <td className="py-3 px-4 text-gray-400">---</td>
                        <td className="py-3 px-4 text-gray-400">---</td>
                        <td className="py-3 px-4 text-gray-400">---</td>
                        <td className="py-3 px-4 text-right font-bold text-red-600">
                          ${parseFloat(p.saldo_pendiente || p.valores?.total || 0).toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Form.Control
                            type="number"
                            size="sm"
                            className={`w-28 text-right font-bold ml-auto ${
                              amountErrors[`e_${p.id_deuda_detalle}`]
                                ? "border-red-500 bg-red-50 text-red-700 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                                : "border-indigo-300 focus:border-indigo-500"
                            }`}
                            value={
                              editableAmounts[`e_${p.id_deuda_detalle}`] ?? ""
                            }
                            onChange={(e) =>
                              handleAmountChange(
                                `e_${p.id_deuda_detalle}`,
                                e.target.value
                              )
                            }
                            onBlur={() =>
                              handleAmountBlur(
                                `e_${p.id_deuda_detalle}`,
                                parseFloat(p.saldo_pendiente || p.valores?.total || 0)
                              )
                            }
                            step="0.01"
                            min="0.01"
                            max={parseFloat(p.saldo_pendiente || p.valores?.total || 0)}
                          />
                          {amountErrors[`e_${p.id_deuda_detalle}`] ? (
                            <span className="text-[10px] text-red-600 font-bold block mt-0.5">
                              {amountErrors[`e_${p.id_deuda_detalle}`]}
                            </span>
                          ) : (
                            <span className="text-[10px] text-indigo-700 font-semibold block mt-0.5">
                              Máx: ${parseFloat(p.saldo_pendiente || p.valores?.total || 0).toFixed(2)}
                            </span>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot className="border-t bg-gray-50">
              <tr>
                <td colSpan="6"></td>
                <td className="py-4 px-4 text-right font-bold text-lg">
                  TOTAL A PAGAR:
                </td>
                <td className="py-4 px-4 text-right font-bold text-xl text-blue-700">
                  ${currentTotal}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0 pb-4 pr-4">
        <Button variant="secondary" onClick={onHide} className="px-4">
          Cancelar
        </Button>
        <Button
          variant="primary"
          disabled={isProcessing || hasErrors}
          onClick={handleLocalConfirm}
          className={`${
            hasErrors ? "bg-red-500 hover:bg-red-600" : "bg-blue-600 hover:bg-blue-700"
          } border-none px-4 transition-colors`}
        >
          {isProcessing ? (
            <Spinner size="sm" animation="border" />
          ) : hasErrors ? (
            "Monto no permitido"
          ) : (
            "Confirmar Pago"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PagoConfirmModal;
